import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
    Building2,
    FileText,
    CheckCircle2,
    ArrowRight,
    Loader2,
    Clock,
    AlertTriangle,
    RefreshCw,
} from 'lucide-react';
import { onboardingAPI, intelligenceAPI, FilledDataset, OnboardingWorkflow, SupplierQualification } from '../../lib/api';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

interface WizardStep {
    id: string;
    title: string;
    description: string;
    icon: React.ReactNode;
    isComplete: boolean;
    isLocked: boolean;
}

interface OnboardingWizardProps {
    workflowId?: number;
    onComplete?: () => void;
}

export default function OnboardingWizard({ workflowId, onComplete: _onComplete }: OnboardingWizardProps) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [workflow, setWorkflow] = useState<OnboardingWorkflow | null>(null);
    const [filledDatasets, setFilledDatasets] = useState<FilledDataset[]>([]);
    const [qualifications, setQualifications] = useState<SupplierQualification[]>([]);
    const [steps, setSteps] = useState<WizardStep[]>([]);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchWorkflowData();
    }, [workflowId]);

    const fetchWorkflowData = async () => {
        setLoading(true);
        try {
            // CR-5900: also pull SupplierQualifications so questionnaire-only
            // onboarding flows (where the customer assigns templates but no
            // legacy datasets) still surface the supplier's outstanding work.
            const [workflowsRes, filledRes, qualsRes] = await Promise.all([
                onboardingAPI.getWorkflows(),
                onboardingAPI.getFilledDatasets(),
                intelligenceAPI.getQualifications({ page_size: 100 }),
            ]);

            const workflows = workflowsRes.data.results || workflowsRes.data || [];
            const filled = filledRes.data.results || filledRes.data || [];
            const quals = qualsRes.data.results || [];

            // Get the specific workflow or the first active one
            const wf = workflowId
                ? workflows.find((w: OnboardingWorkflow) => w.id === workflowId)
                : workflows[0];

            setWorkflow(wf || null);
            setFilledDatasets(filled);
            setQualifications(quals);

            if (wf) {
                buildSteps(wf, filled);
            }
        } catch (error) {
            console.error('Failed to fetch workflow data:', error);
            toast.error('Failed to load onboarding status');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!workflow) return;
        setSubmitting(true);
        try {
            await onboardingAPI.submitWorkflow(workflow.id);
            toast.success('Submitted for approval');
            await fetchWorkflowData();
        } catch (error: unknown) {
            console.error('Failed to submit workflow:', error);
            // CR-4764: Surface the backend's reason (e.g. "Not all required
            // questionnaires are complete") instead of a generic message so
            // the supplier knows what to fix.
            const detail = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
            toast.error(detail || 'Failed to submit for approval');
        } finally {
            setSubmitting(false);
        }
    };

    const buildSteps = (wf: OnboardingWorkflow, filled: FilledDataset[]) => {
        // Count questionnaire completion
        const totalQuestionnaires = filled.length;
        const completedQuestionnaires = filled.filter(
            d => d.status === 'approved' || d.status === 'submitted'
        ).length;

        const questionnairesComplete = totalQuestionnaires > 0 && completedQuestionnaires === totalQuestionnaires;

        const wizardSteps: WizardStep[] = [
            {
                id: 'questionnaires',
                title: 'Questionnaires',
                description: `Complete ${totalQuestionnaires} questionnaire${totalQuestionnaires !== 1 ? 's' : ''}`,
                icon: <FileText className="h-5 w-5" />,
                isComplete: questionnairesComplete,
                isLocked: false,
            },
            {
                id: 'review',
                title: 'Review & Submit',
                description: 'Submit your completed questionnaires for approval',
                icon: <CheckCircle2 className="h-5 w-5" />,
                isComplete: wf.status === 'submitted' || wf.status === 'approved',
                isLocked: !questionnairesComplete,
            },
        ];

        setSteps(wizardSteps);

        // Set current step to first incomplete step
    };

    const handleQuestionnaireClick = (datasetId: number) => {
        navigate(`/onboarding/fill/${datasetId}`);
    };

    // CR-5900: SupplierQualifications resolve to the qualification detail page
    // (not the Approval Register list, which suppliers shouldn't reach).
    const handleQualificationClick = (qualificationId: number) => {
        navigate(`/intelligence/qualifications/${qualificationId}`);
    };

    const getQuestionnaireStatus = (status: string) => {
        const colors: Record<string, string> = {
            not_started: 'bg-gray-100 text-gray-800',
            invited: 'bg-gray-100 text-gray-800',
            in_progress: 'bg-yellow-100 text-yellow-800',
            submitted: 'bg-blue-100 text-blue-800',
            under_review: 'bg-purple-100 text-purple-800',
            more_info_required: 'bg-amber-100 text-amber-800',
            approved: 'bg-green-100 text-green-800',
            rejected: 'bg-red-100 text-red-800',
            expired: 'bg-red-100 text-red-800',
        };
        return colors[status] || colors['not_started'];
    };

    if (loading) {
        return (
            <Card className="w-full max-w-4xl mx-auto">
                <CardContent className="py-12 flex flex-col items-center">
                    <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                    <p className="text-muted-foreground">Loading onboarding status...</p>
                </CardContent>
            </Card>
        );
    }

    // CR-5900: Don't show "No Active Onboarding" if the supplier has any
    // outstanding qualifications -- they were invited via the new
    // QualificationTemplate path and the My Onboarding page is where they
    // expect to find them after clicking the Questionnaire Invitation
    // notification.
    if (!workflow && qualifications.length === 0) {
        return (
            <Card className="w-full max-w-4xl mx-auto">
                <CardContent className="py-12 text-center">
                    <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <h3 className="text-lg font-medium mb-2">No Active Onboarding</h3>
                    <p className="text-muted-foreground">
                        You don't have any active onboarding workflows at this time.
                    </p>
                </CardContent>
            </Card>
        );
    }

    const questionnairesStep = steps.find(s => s.id === 'questionnaires');
    const allComplete = questionnairesStep?.isComplete ?? false;
    const totalQuestionnaires = filledDatasets.length;
    const completedQuestionnaires = filledDatasets.filter(
        d => d.status === 'approved' || d.status === 'submitted'
    ).length;
    const isSubmittedOrApproved = workflow ? (workflow.status === 'submitted' || workflow.status === 'approved') : false;
    // If every filled dataset has already been approved, there is nothing
    // left to submit — the supplier shouldn't see a Submit for Approval
    // button offering to re-submit a completed set. Happens on MAB where
    // the legacy migration landed approved workflows whose FilledDatasets
    // were all pre-approved but workflow.status stayed at its initial value.
    const allDatasetsApproved = totalQuestionnaires > 0 && filledDatasets.every(d => d.status === 'approved');
    // CR-4795: Detect rejected datasets so the "Submitted for approval" banner
    // stops showing after the customer has sent questionnaires back for rework.
    const anyRejected = filledDatasets.some(d => d.status === 'rejected');
    const hideSubmitButton = isSubmittedOrApproved || allDatasetsApproved;

    // CR-5900: Group qualifications by customer ID (not display name -- two
    // customer companies can share the same name, which would collide on the
    // React key) so the supplier can see who invited them when there is no
    // parent onboarding workflow card.
    const qualificationsByCustomer = qualifications.reduce<Record<number, SupplierQualification[]>>((acc, q) => {
        const key = q.customer;
        if (!acc[key]) acc[key] = [];
        acc[key].push(q);
        return acc;
    }, {});

    return (
        <div className="w-full max-w-4xl mx-auto space-y-4">
            {workflow && (
            <Card>
                <CardHeader>
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <CardTitle>Questionnaires</CardTitle>
                            <CardDescription>
                                {isSubmittedOrApproved
                                    ? `Submitted to ${workflow.customer_name} for review`
                                    : `Complete these questionnaires to finish onboarding with ${workflow.customer_name}`}
                            </CardDescription>
                        </div>
                        {totalQuestionnaires > 0 && (
                            <div className="text-right shrink-0">
                                <span className="text-2xl font-bold text-primary">
                                    {completedQuestionnaires}/{totalQuestionnaires}
                                </span>
                                <p className="text-xs text-muted-foreground">complete</p>
                            </div>
                        )}
                    </div>
                </CardHeader>

                <CardContent className="space-y-4">
                    {filledDatasets.length === 0 ? (
                        <div className="text-center py-8">
                            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                            <p className="text-muted-foreground">No questionnaires assigned yet.</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {filledDatasets.map((dataset) => (
                                <div
                                    key={dataset.id}
                                    className="flex flex-col p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className={cn(
                                                'p-2 rounded-full',
                                                dataset.status === 'approved' && 'bg-green-100',
                                                dataset.status === 'submitted' && 'bg-blue-100',
                                                dataset.status === 'in_progress' && 'bg-yellow-100',
                                                dataset.status === 'rejected' && 'bg-red-100',
                                                dataset.status === 'not_started' && 'bg-gray-100'
                                            )}>
                                                {dataset.status === 'approved' ? (
                                                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                                                ) : (
                                                    <FileText className="h-4 w-4 text-muted-foreground" />
                                                )}
                                            </div>
                                            <div>
                                                <h4 className="font-medium">{dataset.dataset_name}</h4>
                                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                    <Badge variant="outline">{dataset.dataset_type}</Badge>
                                                    {(dataset.status === 'approved' || dataset.status === 'submitted' || dataset.status === 'under_review') && dataset.submitted_at ? (
                                                        <span>
                                                            {/* CR-4818: per-dataset "submitted" pre-workflow-submit is
                                                                conceptually "completed" — reserve "Submitted" for the
                                                                workflow-level submission to the customer. */}
                                                            {dataset.status === 'submitted' && !isSubmittedOrApproved ? 'Completed' : 'Submitted'} {new Date(dataset.submitted_at).toLocaleDateString()}
                                                        </span>
                                                    ) : (
                                                        <span>{Math.round(dataset.completion_percentage || 0)}% complete</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            {(() => {
                                                // Renewal pill: lives-longer-than-N-days is healthy, within-30 is
                                                // due-soon, past expiry is expired-and-must-renew. Non-approved rows
                                                // don't show expiry because it isn't set yet.
                                                if (dataset.status !== 'approved' || !dataset.expires_at) return null;
                                                const days = dataset.days_until_expiry;
                                                if (days == null) return null;
                                                if (days < 0) return (
                                                    <Badge variant="destructive" className="gap-1">
                                                        <AlertTriangle className="h-3 w-3" /> Expired — Renew
                                                    </Badge>
                                                );
                                                if (days <= 30) return (
                                                    <Badge className="gap-1 bg-amber-100 text-amber-800 hover:bg-amber-100">
                                                        <Clock className="h-3 w-3" /> Renew in {days}d
                                                    </Badge>
                                                );
                                                return (
                                                    <Badge variant="outline" className="gap-1 text-muted-foreground">
                                                        <CheckCircle2 className="h-3 w-3" /> Up to date · renews in {Math.round(days / 30)}mo
                                                    </Badge>
                                                );
                                            })()}
                                            <Badge className={getQuestionnaireStatus(dataset.status)}>
                                                {/* CR-4818: relabel per-dataset "submitted" → "complete" while the
                                                    workflow itself is still awaiting the supplier's Submit for Approval. */}
                                                {dataset.status === 'submitted' && !isSubmittedOrApproved
                                                    ? 'complete'
                                                    : dataset.status?.replace(/_/g, ' ')}
                                            </Badge>
                                            {(dataset.status === 'not_started' || dataset.status === 'in_progress' || dataset.status === 'more_info_required' || dataset.status === 'rejected') && (
                                                <Button size="sm" onClick={() => handleQuestionnaireClick(dataset.id)}>
                                                    {dataset.status === 'not_started' ? 'Start' : dataset.status === 'rejected' ? 'Resubmit' : 'Continue'}
                                                    <ArrowRight className="h-4 w-4 ml-1" />
                                                </Button>
                                            )}
                                            {(dataset.status === 'submitted' || dataset.status === 'under_review' || dataset.status === 'approved') && (
                                                <Button size="sm" variant="outline" onClick={() => handleQuestionnaireClick(dataset.id)}>
                                                    <FileText className="h-4 w-4 mr-1" /> View responses
                                                </Button>
                                            )}
                                            {dataset.status === 'approved' && dataset.days_until_expiry != null && dataset.days_until_expiry <= 30 && (
                                                <Button size="sm" onClick={() => handleQuestionnaireClick(dataset.id)}>
                                                    <RefreshCw className="h-4 w-4 mr-1" /> Renew
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    {dataset.status === 'rejected' && dataset.rejection_reason && (
                                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                                            <p className="text-sm font-medium text-red-800">Rejection Reason:</p>
                                            <p className="text-sm text-red-700 mt-1">{dataset.rejection_reason}</p>
                                        </div>
                                    )}
                                    {dataset.status === 'more_info_required' && dataset.more_info_feedback && (
                                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                            <p className="text-sm font-medium text-amber-800">Additional Information Required:</p>
                                            <p className="text-sm text-amber-700 mt-1">{dataset.more_info_feedback}</p>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* CR-4795: Rejected state takes precedence — once the
                        customer sends any questionnaire back for rework the
                        "customer will review shortly" banner is no longer true. */}
                    {anyRejected ? (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                            <p className="font-medium text-red-800">
                                Questionnaires have been reviewed and rejected. Please resubmit for approval.
                            </p>
                        </div>
                    ) : isSubmittedOrApproved && !allDatasetsApproved && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                            <CheckCircle2 className="h-6 w-6 text-green-600 mx-auto mb-2" />
                            <p className="font-medium text-green-800">
                                Submitted for approval — the customer will review shortly.
                            </p>
                        </div>
                    )}
                    {totalQuestionnaires > 0 && !hideSubmitButton && (
                        <Button
                            className="w-full"
                            size="lg"
                            disabled={submitting || !allComplete}
                            onClick={handleSubmit}
                        >
                            {submitting ? (
                                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting...</>
                            ) : (
                                <><CheckCircle2 className="h-4 w-4 mr-2" /> Submit for Approval</>
                            )}
                        </Button>
                    )}
                </CardContent>
            </Card>
            )}

            {/* CR-5900: Render SupplierQualifications grouped by customer.
                Suppliers reach this card from the Questionnaire Invitation
                notification; the Approval Register at /intelligence/qualifications
                used to host them and is the wrong destination for an unapproved
                supplier. */}
            {Object.entries(qualificationsByCustomer).map(([customerId, customerQuals]) => {
                const customerName = customerQuals[0]?.customer_name || 'Customer';
                return (
                <Card key={customerId}>
                    <CardHeader>
                        <CardTitle>Questionnaires</CardTitle>
                        <CardDescription>
                            Complete these questionnaires for {customerName}
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        {customerQuals.map((qual) => (
                            <div
                                key={qual.id}
                                className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                            >
                                <div className="flex items-center gap-3">
                                    <div className={cn(
                                        'p-2 rounded-full',
                                        qual.status === 'approved' && 'bg-green-100',
                                        (qual.status === 'submitted' || qual.status === 'under_review') && 'bg-blue-100',
                                        qual.status === 'in_progress' && 'bg-yellow-100',
                                        qual.status === 'rejected' && 'bg-red-100',
                                        (qual.status === 'invited' || qual.status === 'more_info_required') && 'bg-gray-100',
                                    )}>
                                        {qual.status === 'approved' ? (
                                            <CheckCircle2 className="h-4 w-4 text-green-600" />
                                        ) : (
                                            <FileText className="h-4 w-4 text-muted-foreground" />
                                        )}
                                    </div>
                                    <div>
                                        <h4 className="font-medium">{qual.template_name || `Questionnaire #${qual.template}`}</h4>
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <Badge variant="outline">Questionnaire</Badge>
                                            {qual.completion_percentage != null && (
                                                <span>{Math.round(qual.completion_percentage)}% complete</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Badge className={getQuestionnaireStatus(qual.status)}>
                                        {qual.status_display || qual.status?.replace(/_/g, ' ')}
                                    </Badge>
                                    {(qual.status === 'invited' || qual.status === 'in_progress' || qual.status === 'more_info_required' || qual.status === 'rejected') && (
                                        <Button size="sm" onClick={() => handleQualificationClick(qual.id)}>
                                            {qual.status === 'invited' ? 'Start' : qual.status === 'rejected' ? 'Resubmit' : 'Continue'}
                                            <ArrowRight className="h-4 w-4 ml-1" />
                                        </Button>
                                    )}
                                    {(qual.status === 'submitted' || qual.status === 'under_review' || qual.status === 'approved') && (
                                        <Button size="sm" variant="outline" onClick={() => handleQualificationClick(qual.id)}>
                                            <FileText className="h-4 w-4 mr-1" /> View responses
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </CardContent>
                </Card>
                );
            })}
        </div>
    );
}
